import { Grid } from '@mantine/core';
import { Control, Controller, Path } from 'react-hook-form';

import DatePickerWrapper from '@/ui/DatePickerWrapper';
import Dropdown from '@/ui/Dropdown';
import LabelText from '@/ui/LabelText';
import TextInputWrapper from '@/ui/TextInputWrapper';

import {
  DEVICE_TYPE_OPTIONS,
  MANUFACTURER_OPTIONS
} from '../TransmissionReportPage/DeviceInfo/DeviceEditModal';

type DeviceInfoFieldsProps<T extends Record<string, any>> = {
  control: Control<T>;
  vendor: string;
};

const DeviceInfoFields = <T extends Record<string, any>>({
  control,
  vendor
}: DeviceInfoFieldsProps<T>) => {
  return (
    <Grid>
      <Grid.Col span={{ base: 12, sm: 4 }}>
        <Controller
          control={control}
          name={'vendor' as Path<T>}
          render={({ field }) => (
            <Dropdown
              label="Manufacturer"
              required
              labelVariant="regular"
              options={MANUFACTURER_OPTIONS}
              value={MANUFACTURER_OPTIONS.find((opt) => opt.value === field.value)}
              onChange={(option) => field.onChange(option?.value)}
              placeholder="Select Manufacturer"
            />
          )}
        />
      </Grid.Col>

      <Grid.Col span={{ base: 12, sm: 4 }}>
        <Controller
          control={control}
          name={'type' as Path<T>}
          render={({ field }) => (
            <Dropdown
              label="Type"
              required
              labelVariant="regular"
              options={DEVICE_TYPE_OPTIONS}
              value={DEVICE_TYPE_OPTIONS.find((opt) => opt.value === field.value)}
              onChange={(option) => field.onChange(option?.value)}
              placeholder="Select Type"
            />
          )}
        />
      </Grid.Col>

      <Grid.Col span={{ base: 12, sm: 4 }}>
        <Controller
          control={control}
          name={'serialNumber' as Path<T>}
          render={({ field }) => (
            <TextInputWrapper
              required
              label="Serial Number"
              value={field.value ?? ''}
              onChange={field.onChange}
              placeholder="Input Serial Number"
            />
          )}
        />
      </Grid.Col>

      <Grid.Col span={{ base: 12, sm: 4 }}>
        <Controller
          control={control}
          name={'modelName' as Path<T>}
          render={({ field }) => (
            <TextInputWrapper
              required={vendor === 'biotronik'}
              label="Model Name"
              value={field.value ?? ''}
              onChange={field.onChange}
              placeholder="Input Model Name"
            />
          )}
        />
      </Grid.Col>

      <Grid.Col span={{ base: 12, sm: 4 }}>
        <Controller
          control={control}
          name={'modelNumber' as Path<T>}
          render={({ field }) => (
            <TextInputWrapper
              required={vendor !== 'biotronik'}
              label="Model Number"
              value={field.value ?? ''}
              onChange={field.onChange}
              placeholder="Input Model Number"
            />
          )}
        />
      </Grid.Col>

      <Grid.Col span={{ base: 12, sm: 4 }}>
        <LabelText label="Implant Date" required variant="regular" boxProps={{ mb: 4 }} />
        <Controller
          control={control}
          name={'implantDate' as Path<T>}
          render={({ field }) => (
            <DatePickerWrapper
              inputProps={{ placeholder: 'MM/DD/YYYY', required: true }}
              datePickerProps={{
                id: 'implantDate',
                selected: field.value && field.value !== '-' ? new Date(field.value) : null,
                onChange: (date: Date | null) => field.onChange(date),
                required: true,
                isClearable: false
              }}
            />
          )}
        />
      </Grid.Col>
    </Grid>
  );
};

export default DeviceInfoFields;
